import {
  LiquidGlassView as LiquidGlassViewFromLiquidGlass,
  isLiquidGlassSupported,
} from "@-/com.npmjs.callstack__liquid-glass";
import { cn } from "@-/com.npmjs.cn";
import { withUniwind } from "@-/com.npmjs.uniwind";
import type { ViewProps } from "@-/com.npmjs.react-native";
import { Platform, View } from "@-/com.npmjs.react-native";
import { FrostedSurface } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useToolbarGlassTint } from "#use-toolbar-glass-tint";
import {
  type GlassAppearance,
  GlassContentContext,
} from "@-/dev.improve.haniverse.components.ui.text";
import {
  type ComponentProps,
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";

const LiquidGlassView = withUniwind(LiquidGlassViewFromLiquidGlass);

/** Reports a glass's appearance to the nearest `GlassContentScope` (and on up to its parents). */
const GlassAppearanceReporterContext = createContext<
  ((appearance: GlassAppearance) => void) | null
>(null);

/**
 * Everything drawn on a Liquid Glass surface, including labels and icons that overlay the glass
 * rather than sit inside it. The glass inside reports the light or dark appearance iOS picks for
 * its backdrop, and `Text` / `Icon` here take the matching ink, so nothing hard-codes light or
 * dark per background. Off iOS Liquid Glass the context stays `null` and the theme inks apply.
 */
export function GlassContentScope({ children }: PropsWithChildren) {
  const reportToParent = useContext(GlassAppearanceReporterContext);
  const [appearance, setAppearance] = useState<GlassAppearance | null>(null);
  const report = useCallback(
    (next: GlassAppearance) => {
      setAppearance(next);
      reportToParent?.(next);
    },
    [reportToParent],
  );
  return (
    <GlassAppearanceReporterContext value={report}>
      <GlassContentContext value={isLiquidGlassSupported ? appearance : null}>
        {children}
      </GlassContentContext>
    </GlassAppearanceReporterContext>
  );
}

/**
 * The glass itself, reporting its appearance into the enclosing `GlassContentScope`.
 *
 * The native view keeps its own glass showing (see the liquid-glass patch's README). It reports
 * here when UIKit dropped a glass for no reason the view could see, or would not show one it
 * assigned. Neither should happen, and the view retries by itself: the log line is how such a
 * case on a device in the field becomes visible.
 */
function ReportingLiquidGlassView(props: ComponentProps<typeof LiquidGlassView>) {
  const report = useContext(GlassAppearanceReporterContext);
  return (
    <LiquidGlassView
      onAppearanceChange={(event) => report?.(event.nativeEvent.appearance)}
      onGlassIssue={(event) =>
        logger(import.meta.location).warn("Liquid Glass issue", event.nativeEvent.message)
      }
      {...props}
    />
  );
}

/**
 * The edge a flat stand-in for Liquid Glass (Android's frost, iOS before 26) draws where the glass
 * would show its lit rim, so the control weighs the same as the bordered meters and tiles beside
 * it. An overlay, so the border takes no layout space and the control keeps its glass size.
 */
function FlatGlassBorder() {
  return (
    <View className="absolute inset-0 rounded-full border border-border" pointerEvents="none" />
  );
}

/**
 * A toolbar control's glass, tinted by `useToolbarGlassTint`. iOS picks the glass's light or dark
 * appearance from its backdrop (`colorScheme="system"`), and its content's ink follows
 * (`GlassContentScope`).
 */
export function ToolbarGlassSurface({
  children,
  className,
  interactive = true,
  selected = false,
  ...props
}: ViewProps & { interactive?: boolean; selected?: boolean }) {
  const tintColor = useToolbarGlassTint(selected);
  if (Platform.OS === "android") {
    return (
      <FrostedSurface {...props} className={cn("rounded-full", className)} selected={selected}>
        {children}
        <FlatGlassBorder />
      </FrostedSurface>
    );
  }
  return (
    <GlassContentScope>
      <ReportingLiquidGlassView
        className={cn(
          "rounded-full",
          !isLiquidGlassSupported && (selected ? "bg-accent" : "bg-background"),
          className,
        )}
        colorScheme="system"
        effect="regular"
        interactive={interactive}
        tintColor={tintColor}
        {...props}
      >
        {children}
        {!isLiquidGlassSupported && Platform.OS === "ios" ? <FlatGlassBorder /> : null}
      </ReportingLiquidGlassView>
    </GlassContentScope>
  );
}
